import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const CurrencyContext = createContext();

export const useCurrency = () => {
    const context = useContext(CurrencyContext);
    if (!context) {
        throw new Error('useCurrency must be used within CurrencyProvider');
    }
    return context;
};

export const CurrencyProvider = ({ children }) => {
    const [displayCurrency, setDisplayCurrency] = useState('TRY');
    const [rates, setRates] = useState({ USD: 1, EUR: 1, TRY: 1 }); // Base TRY for now or relative
    // Actually we need current rates to convert. 
    // Should we fetch them here or pass them? fetch is better.

    useEffect(() => {
        // Fetch base rates (USD, EUR) against TRY
        const fetchRates = async () => {
            try {
                const res = await axios.get('/api/currency');
                // Adjust endpoint as needed. Looking at routes/currency.js helps.
                // Assuming response is array of currencies.
                if (res.data) {
                    const newRates = { TRY: 1 };
                    res.data.forEach(c => {
                        if (c.code === 'USD' || c.code === 'USDTRY=X') newRates.USD = c.sellPrice;
                        if (c.code === 'EUR' || c.code === 'EURTRY=X') newRates.EUR = c.sellPrice;
                    });
                    setRates(newRates);
                }
            } catch (error) {
                console.error('Rates fetch error:', error);
            }
        };
        fetchRates();
    }, []);

    // Helpers
    const formatPrice = (valueInTry) => {
        if (!valueInTry) return '0.00';

        if (displayCurrency === 'TRY') return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(valueInTry);

        const rate = rates[displayCurrency] || 1;
        const value = valueInTry / rate;

        return new Intl.NumberFormat('en-US', { style: 'currency', currency: displayCurrency }).format(value);
    };

    // Converted value formatted as number (no symbol)
    const formatValue = (valueInTry) => {
        if (!valueInTry && valueInTry !== 0) return '0.00';

        let value = valueInTry;
        if (displayCurrency !== 'TRY') {
            const rate = rates[displayCurrency] || 1;
            value = valueInTry / rate;
        }

        return new Intl.NumberFormat(displayCurrency === 'TRY' ? 'tr-TR' : 'en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    // Just value converter
    const convertPrice = (valueInTry) => {
        if (displayCurrency === 'TRY') return valueInTry;
        const rate = rates[displayCurrency] || 1;
        return valueInTry / rate;
    };

    const getSymbol = () => {
        if (displayCurrency === 'USD') return '$';
        if (displayCurrency === 'EUR') return '€';
        return '₺';
    }

    return (
        <CurrencyContext.Provider value={{ displayCurrency, setDisplayCurrency, formatPrice, formatValue, convertPrice, getSymbol }}>
            {children}
        </CurrencyContext.Provider>
    );
};
